import {
    watch,
    computed,
    defineComponent,
    ref,
} from "vue";

import { useLocale }    from "../../locales/i18n.js";
import ImageFallback    from "../../components/imageFallback.js";

import {
    getMemberAvatar,
    getMemberDisplayName,
    getMemberTag
} from "../../utils.js";

const PresenceActivity = defineComponent({

    props: [ "activity" ],

    setup(props) {
        const { i18n }          = useLocale();
        const refActivityRpc    = ref(null);

        const refLargeImageSrc = computed(() => {
            if (props.activity.type === 2)
                if (props.activity.name === "Spotify")
                    return `https://i.scdn.co/image/${props.activity.assets.largeImage.split(":")[1]}`;

            if (props.activity.assets?.largeImage)
                return props.activity.assets.largeImage;

            if (refActivityRpc.value?.icon)
                return `https://cdn.discordapp.com/app-icons/${refActivityRpc.value.id}/${refActivityRpc.value.icon}.png`;
        });

        const refLargeText = computed(() => {
            return props.activity.assets?.largeText
                || props.activity.name;
        });

        function renderName() {
            const Types = [
                i18n("presence_activity.types.play"),
                i18n("presence_activity.types.stream"),
                i18n("presence_activity.types.listen"),
                i18n("presence_activity.types.watching"),
                null,   // 4: Custom
                i18n("presence_activity.types.competing"),
            ];

            if (props.activity.type === 4)
                return "Custom";

            return `${Types[props.activity.type]} ${props.activity.name}`;
        }

        function renderImage() {
            console.log(props.activity);

            if (refLargeImageSrc.value)
                return <ImageFallback class="log-presence-activity-assets-image" src={refLargeImageSrc.value} alt={refLargeText.value} default="https://cdn.discordapp.com/embed/avatars/0.png" />;

            if (props.activity.type === 4 && props.activity.emoji)
                return props.activity.emoji.imageURL
                    ? <ImageFallback class="log-presence-activity-assets-image" src={props.activity.emoji.imageURL} alt={refLargeText.value} default="https://cdn.discordapp.com/embed/avatars/0.png" />
                    : <div class="log-presence-activity-assets-image">{props.activity.emoji.name}</div>;
        }

        watch(() => props.activity, async (activity) => {
            if (activity.applicationId) {
                const res = await fetch(`https://discord.com/api/v10/applications/${activity.applicationId}/rpc`)
                    .then((res) => res.json());

                refActivityRpc.value = res;

            } else {
                refActivityRpc.value = null;
            }
        }, { immediate: true });

        return () => {
            const imageNode = renderImage(props.activity);

            return (
                <div class="log-presence-activity">
                    <div class="log-presence-activity-name">
                        {renderName(props.activity)}
                    </div>

                    <div class="log-presence-activity-content">
                        {imageNode && (
                            <div class="log-presence-activity-assets-container">
                                {imageNode}
                            </div>
                        )}

                        <div class="log-presence-activity-details">
                            <div class="log-presence-activity-state">
                                {props.activity.state || props.activity.name}
                            </div>

                            {props.activity.details && (
                                <div class="log-presence-activity-state-details">
                                    {props.activity.details}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            );
        };
    },
});

export default {

    props: [ "log", "members" ],

    setup(props) {
        const { i18n }      = useLocale();
        const refLogPreview = ref(null);

        return () => {
            const member = props.members.value.get(props.log.executor);

            if (props.log.type.startsWith("presence_"))
                return (
                    <div class="log-preview" ref={refLogPreview}>
                        <div class="log-presence">
                            <div class="log-presence-title">
                                {i18n("logs_presence_new.title")}
                            </div>

                            <div class="log-presence-user">
                                <div class="log-presence-avatar-container">
                                    <ImageFallback class="log-presence-avatar-image" src={getMemberAvatar(member)} default="https://cdn.discordapp.com/embed/avatars/0.png" alt={getMemberDisplayName(member)} />

                                    <div class={[ "log-presence-avatar-presence", `log-presence-status-${props.log.extra.newPresence.status}` ]} />
                                </div>

                                <div class="log-presence-name">
                                    <div class="log-presence-displayname">
                                        {getMemberDisplayName(member)}
                                    </div>

                                    <div class="log-presence-username">
                                        {getMemberTag(member)}
                                    </div>
                                </div>
                            </div>

                            <div class="log-presence-platforms">
                                <div class={[ "material-symbols-outlined", "log-presence-platform-desktop", `log-presence-status-${props.log.extra.newPresence.clientStatus.desktop || "offline"}` ]}>
                                    computer
                                </div>

                                <div class={[ "material-symbols-outlined", "log-presence-platform-mobile", `log-presence-status-${props.log.extra.newPresence.clientStatus.mobile || "offline"}` ]}>
                                    smartphone
                                </div>

                                <div class={[ "material-symbols-outlined", "log-presence-platform-web", `log-presence-status-${props.log.extra.newPresence.clientStatus.web || "offline"}` ]}>
                                    public
                                </div>

                                <div class={[ "material-symbols-outlined", "log-presence-platform-embedded", `log-presence-status-${props.log.extra.newPresence.clientStatus.embedded || "offline"}` ]}>
                                    sports_esports
                                </div>
                            </div>

                            {!!props.log.extra.newPresence.activities?.length && (
                                <div class="log-presence-activities">
                                    {props.log.extra.newPresence.activities.map((activity) => (
                                        <PresenceActivity activity={activity} />
                                    ))}
                                </div>
                            )}
                        </div>

                        {props.log.extra.oldPresence && (
                            <div class="log-presence">
                                <div class="log-presence-title">
                                    {i18n("logs_presence_old.title")}
                                </div>

                                <div class="log-presence-user">
                                    <div class="log-presence-avatar-container">
                                        <ImageFallback class="log-presence-avatar-image" src={getMemberAvatar(member)} default="https://cdn.discordapp.com/embed/avatars/0.png" alt={getMemberDisplayName(member)} />

                                        <div class={[ "log-presence-avatar-presence", `log-presence-status-${props.log.extra.oldPresence.status}` ]} />
                                    </div>

                                    <div class="log-presence-name">
                                        <div class="log-presence-displayname">
                                            {getMemberDisplayName(member)}
                                        </div>

                                        <div class="log-presence-username">
                                            {getMemberTag(member)}
                                        </div>
                                    </div>
                                </div>

                                <div class="log-presence-platforms">
                                    <div class={[ "material-symbols-outlined", "log-presence-platform-desktop", `log-presence-status-${props.log.extra.oldPresence.clientStatus.desktop || "offline"}` ]}>
                                        computer
                                    </div>

                                    <div class={[ "material-symbols-outlined", "log-presence-platform-mobile", `log-presence-status-${props.log.extra.oldPresence.clientStatus.mobile || "offline"}` ]}>
                                        smartphone
                                    </div>

                                    <div class={[ "material-symbols-outlined", "log-presence-platform-web", `log-presence-status-${props.log.extra.oldPresence.clientStatus.web || "offline"}` ]}>
                                        public
                                    </div>

                                    <div class={[ "material-symbols-outlined", "log-presence-platform-embedded", `log-presence-status-${props.log.extra.oldPresence.clientStatus.embedded || "offline"}` ]}>
                                        sports_esports
                                    </div>
                                </div>

                                {!!props.log.extra.oldPresence.activities?.length && (
                                    <div class="log-presence-activities">
                                        {props.log.extra.oldPresence.activities.map((activity) => (
                                            <PresenceActivity activity={activity} />
                                        ))}
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                );

            return (
                <div class="log-preview">
                    <div class="log-entry">
                        Log pas encore supportée.
                    </div>
                </div>
            );
        };
    },
}
